import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeft, Download, Mail, ArrowRight } from 'lucide-react';

const Leadership = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link
              to="/about"
              className="inline-flex items-center text-blue-200 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="mr-2" size={20} />
              {t('nav.about')}
            </Link>
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="leadership-title">
                Executive Profile
              </h1>
              <p className="text-xl text-blue-200 max-w-3xl mx-auto">
                Meet the leadership driving Selimarine's marine and energy excellence
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* One-Pager Display */}
      <section className="py-16 bg-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* One-pager image */}
            <div className="relative bg-slate-900">
              <img 
                src="https://customer-assets.emergentagent.com/job_web-refresh-plan/artifacts/uuwab0qx_Sinan%20SM%20visual%20CV%2005.2026.png"
                alt="Muhammet Sinan İrtemek - Project Executive Profile"
                className="w-full h-auto"
                data-testid="executive-onepager"
              />
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {/* Download Profile */}
            <a
              href="https://customer-assets.emergentagent.com/job_web-refresh-plan/artifacts/uuwab0qx_Sinan%20SM%20visual%20CV%2005.2026.png"
              download="Selimarine-Executive-Profile.png"
              className="bg-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition-all group"
              data-testid="download-profile"
            >
              <div className="flex items-center space-x-4">
                <div className="bg-amber-500 p-4 rounded-lg group-hover:bg-amber-600 transition-colors">
                  <Download className="text-white" size={28} />
                </div>
                <div className="flex-grow">
                  <h3 className="text-lg font-bold text-slate-900 mb-1">
                    Download Executive Profile
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Save the complete one-page CV for your records
                  </p>
                </div>
                <ArrowRight className="text-amber-500 group-hover:translate-x-1 transition-transform" size={20} />
              </div>
            </a>

            {/* Contact CTA */}
            <Link
              to="/contact"
              className="bg-gradient-to-br from-blue-900 to-slate-900 p-6 rounded-xl shadow-lg hover:shadow-2xl transition-all group text-white"
              data-testid="contact-from-leadership"
            >
              <div className="flex items-center space-x-4">
                <div className="bg-amber-500 p-4 rounded-lg group-hover:bg-amber-600 transition-colors">
                  <Mail className="text-white" size={28} />
                </div>
                <div className="flex-grow">
                  <h3 className="text-lg font-bold mb-1">
                    Discuss Your Project
                  </h3>
                  <p className="text-blue-200 text-sm">
                    Get in touch with our leadership team
                  </p>
                </div>
                <ArrowRight className="text-amber-400 group-hover:translate-x-1 transition-transform" size={20} />
              </div>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Key Strengths Summary */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Leadership That Delivers
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Two decades of proven expertise in marine and energy projects across challenging geographies
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center p-6 bg-blue-50 rounded-xl">
              <div className="text-4xl font-bold text-blue-900 mb-2">15+</div>
              <div className="text-sm text-gray-600">Years International Experience</div>
            </div>
            <div className="text-center p-6 bg-amber-50 rounded-xl">
              <div className="text-4xl font-bold text-amber-700 mb-2">240</div>
              <div className="text-sm text-gray-600">MW Power Capacity Delivered</div>
            </div>
            <div className="text-center p-6 bg-green-50 rounded-xl">
              <div className="text-4xl font-bold text-green-800 mb-2">10+</div>
              <div className="text-sm text-gray-600">Major Projects</div>
            </div>
            <div className="text-center p-6 bg-purple-50 rounded-xl">
              <div className="text-4xl font-bold text-purple-800 mb-2">8</div>
              <div className="text-sm text-gray-600">Countries</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Leadership;
